// =================================================================
// InputToolbar.js - Toolbar Input Enrichi Oracle TRYA + Nouvelles Fonctions
// Outils contextuels : Analyser/Résumé/PUBLIER/RÉSEAUX/Templates/Vider/Base Expertise
// =================================================================

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, FileText, Layout, Trash2, Database, Upload, Share2, ChevronDown, Settings, Pencil, HelpCircle, GitCommit } from 'lucide-react';
import { toast } from '../ui/Toast';

const SYSTEM_TEMPLATES = [
  { category: 'Analyse', items: [
    { label: 'Diagnostic projet', prompt: 'Fais un diagnostic complet de ce projet : forces, faiblesses, risques et opportunités. Propose un plan d\'action priorisé.' },
    { label: 'Points bloquants', prompt: 'Identifie les points bloquants actuels du projet et propose des solutions concrètes pour chacun.' },
    { label: 'État d\'avancement', prompt: 'Fais le point sur l\'état d\'avancement du projet. Qu\'est-ce qui a été accompli, qu\'est-ce qui reste à faire ?' },
  ]},
  { category: 'Rédaction', items: [
    { label: 'Compte-rendu', prompt: 'Rédige un compte-rendu professionnel de la dernière session de travail sur ce projet.' },
    { label: 'Email de suivi', prompt: 'Rédige un email de suivi professionnel pour informer l\'équipe de l\'avancement du projet.' },
    { label: 'Documentation technique', prompt: 'Génère une documentation technique structurée de l\'état actuel du projet.' },
  ]},
  { category: 'Stratégie', items: [
    { label: 'Prochaines étapes', prompt: 'Propose les 5 prochaines étapes prioritaires pour faire avancer ce projet. Sois concret et actionnable.' },
    { label: 'Brainstorm', prompt: 'Lance un brainstorming créatif sur ce projet. Propose des idées innovantes, même audacieuses.' },
    { label: 'Risques & mitigation', prompt: 'Analyse les risques potentiels du projet et propose des stratégies de mitigation pour chacun.' },
  ]},
];

const InputToolbar = ({
  onAnalyze,
  onSummary,
  onInsertTemplate,
  onClear,
  onBaseExpertise,
  onManageTemplates,
  onEditTemplate,
  helpMode = false,
  onToggleHelp,
  onConsolidate,
  projectId,
  currentProject,
  messageCount = 0,
  forcedModel = null,
  onForcedModelChange,
  isStreaming = false,
  templatesVersion = 0,
}) => {

  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const templatesRef = useRef(null);
  const [availableModels, setAvailableModels] = useState([]);
  const modelDropdownRef = useRef(null);

  // Rôle + template décodés depuis le JWT pour filtrer Haiku.
  // Haiku reste accessible uniquement pour admin_general (Ben) — tâches rapides
  // internes. Banni pour oracle_fondateur (Pierre, Annie) et users template (Greg,
  // futurs Joe/Laurent) car hallucinations structurelles sur les sujets ITRIIA
  // même avec garde-fou §5 explicite (cf. session Greg NEXT).
  const currentUserMeta = React.useMemo(() => {
    try {
      const token = localStorage.getItem('oracle_token');
      if (!token) return { role: null, template: null };
      const payload = JSON.parse(atob(token.split('.')[1]));
      return { role: payload.role || null, template: payload.profile_template_code || null };
    } catch { return { role: null, template: null }; }
  }, []);

  const handleModelSelect = (mid) => {
    onForcedModelChange(mid);
    setShowModelDropdown(false);
  };

  // Charger les modèles depuis le backend
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const token = localStorage.getItem('oracle_token');
        const res = await axios.get('/api/models?context=chat', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAvailableModels(res.data.models || []);
      } catch (err) {
        console.error('Erreur chargement modèles:', err);
      }
    };
    fetchModels();
  }, []);

  // Fermer les dropdowns au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target)) {
        setShowModelDropdown(false);
      }
      if (templatesRef.current && !templatesRef.current.contains(e.target)) {
        setShowTemplates(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Haiku réservé à admin_general (Ben) pour tâches rapides internes.
  // Tout autre rôle (oracle_fondateur, collaborateur, users template) est filtré.
  const modelsForDisplay = availableModels.filter(m => {
    if (currentUserMeta.role === 'admin_general') return true;
    const mid = (m.model_id || m.id || '').toLowerCase();
    return !/haiku/.test(mid);
  });

  // Catégorie « Thinking » (raisonnement) : modèles tagués category:'thinking'
  // (via metadata DB, ou champ direct en fallback hardcodé), affichés EN TÊTE, triés par rank.
  const catOf = (m) => (m.metadata && m.metadata.category) || m.category || null;
  const rankOf = (m) => (m.metadata && m.metadata.rank) ?? m.rank ?? 999;
  const thinkingModels = modelsForDisplay
    .filter(m => catOf(m) === 'thinking')
    .sort((a, b) => rankOf(a) - rankOf(b));
  const thinkingIds = new Set(thinkingModels.map(m => m.model_id || m.id));

  // Les autres modèles restent groupés par provider, sous la catégorie Thinking.
  const groupedModels = modelsForDisplay
    .filter(m => !thinkingIds.has(m.model_id || m.id))
    .reduce((acc, model) => {
      if (!acc[model.provider]) acc[model.provider] = [];
      acc[model.provider].push(model);
      return acc;
    }, {});

  const selectedModelLabel = forcedModel
    ? (availableModels.find(m => (m.model_id || m.id) === forcedModel)?.label || forcedModel)
    : 'Auto';

  const tools = [
    {
      id: 'analyze',
      label: 'Analyser',
      icon: <Search size={14} />,
      action: onAnalyze,
      description: 'Analyse complète contexte projet',
      color: '#3b82f6'
    },
    {
      id: 'summary',
      label: 'Résumé',
      icon: <FileText size={14} />,
      action: onSummary,
      description: 'Résumé intelligent conversation',
      color: '#8b5cf6'
    },
    ...(onConsolidate && messageCount >= 10 ? [{
      id: 'consolidate',
      label: 'Consolider',
      icon: <GitCommit size={14} />,
      action: onConsolidate,
      description: 'Transforme la conversation en synthèse structurée (mémoire de référence)',
      color: '#1D4ED8',
      isFeatured: true,
    }] : []),
    {
      id: 'publish',
      label: 'PUBLIER',
      icon: <Upload size={14} />,
      action: () => setShowPublishModal(true),
      description: 'Publication multi-canaux professionnels',
      color: '#059669',
      isNew: true
    },
    {
      id: 'social',
      label: 'RÉSEAUX',
      icon: <Share2 size={14} />,
      action: () => setShowSocialModal(true),
      description: 'Diffusion réseaux sociaux automatisée',
      color: '#7c3aed',
      isNew: true
    },
    {
      id: 'templates',
      label: 'Raccourcis',
      icon: <Layout size={14} />,
      action: () => setShowTemplates(!showTemplates),
      description: 'Modèles prédéfinis contextuels',
      color: '#06b6d4'
    },
    {
      id: 'help',
      label: helpMode ? 'Aide ON' : 'Aide',
      icon: <HelpCircle size={14} />,
      action: onToggleHelp,
      description: helpMode ? 'Désactiver le mode aide' : 'Consulter le guide ITRIIA',
      color: '#10b981',
      isActive: helpMode,
    },
    {
      id: 'clear',
      label: 'Nouvelle conv.',
      icon: <Trash2 size={14} />,
      action: onClear,
      description: 'Nouvelle conversation (la mémoire est préservée)',
      color: '#3b82f6'
    },
    {
      id: 'expertise',
      label: 'Base Expertise',
      icon: <Database size={14} />,
      action: onBaseExpertise,
      description: 'Base Expertise Métier + concepts',
      color: '#1D4ED8'
    }
  ];

  // Templates dynamiques — user templates prioritaires, sinon système
  const [userTemplates, setUserTemplates] = useState([]);
  const [templatesLoadedOnce, setTemplatesLoadedOnce] = useState(false);

  const loadUserTemplates = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await axios.get('/api/templates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserTemplates(res.data?.templates || []);
    } catch (err) {
      console.error('Erreur chargement templates:', err);
    } finally {
      setTemplatesLoadedOnce(true);
    }
  };

  useEffect(() => { loadUserTemplates(); }, [templatesVersion]);

  // Templates user + système — user en premier, système toujours présents
  const templates = (() => {
    const result = [];
    // Templates personnels d'abord
    if (userTemplates.length > 0) {
      const byCategory = {};
      userTemplates.forEach(t => {
        if (!byCategory[t.category]) byCategory[t.category] = [];
        byCategory[t.category].push({ label: t.title, prompt: t.content, isUser: true, id: t.id });
      });
      Object.entries(byCategory).forEach(([category, items]) => {
        result.push({ category: '★ ' + category, items });
      });
    }
    // Puis templates système
    SYSTEM_TEMPLATES.forEach(cat => {
      result.push({ category: cat.category, items: cat.items.map(t => ({ ...t, isSystem: true })) });
    });
    return result;
  })();

  // Modal Publication Professionnelle
  const PublishModal = () => (
    <div className="modal-overlay" onClick={() => setShowPublishModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            📝 Publication Multi-Canaux
            <span style={{ fontSize: '10.5px', fontWeight: 600, marginLeft: 10, padding: '2px 8px', borderRadius: 4, background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.30)', textTransform: 'uppercase', letterSpacing: 0.5, verticalAlign: 'middle' }}>En cours</span>
          </h3>
          <button className="modal-close" onClick={() => setShowPublishModal(false)}>×</button>
        </div>

        <div className="modal-body">
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16, padding: '10px 12px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.18)', borderRadius: 6, lineHeight: 1.5 }}>
            Feature en cours de développement — la publication effective vers les canaux ci-dessous n'est pas encore active. Les destinations sont présentées à titre d'aperçu.
          </div>
          <div className="publish-destinations">
            <label className="destination-option">
              <input type="checkbox" defaultChecked />
              <div className="destination-info">
                <span className="destination-name">📚 Base Expertise Oracle TRYA</span>
                <span className="destination-desc">Documentation interne + indexation Weaviate</span>
              </div>
            </label>

            <label className="destination-option">
              <input type="checkbox" defaultChecked />
              <div className="destination-info">
                <span className="destination-name">🌐 Votre blog</span>
                <span className="destination-desc">WordPress + Elementor + SEO optimisé</span>
              </div>
            </label>
            
            <label className="destination-option">
              <input type="checkbox" />
              <div className="destination-info">
                <span className="destination-name">📧 Email Équipe</span>
                <span className="destination-desc">Notification équipe fondatrice avec contenu</span>
              </div>
            </label>
            
            <label className="destination-option">
              <input type="checkbox" />
              <div className="destination-info">
                <span className="destination-name">💾 Sauvegarde Projet</span>
                <span className="destination-desc">Archivage conversation + métadonnées</span>
              </div>
            </label>
          </div>
          
          <div className="publish-options">
            <div className="option-group">
              <label>📅 Publication</label>
              <select className="option-select">
                <option>Immédiate</option>
                <option>Programmée</option>
                <option>Brouillon</option>
              </select>
            </div>
            
            <div className="option-group">
              <label>🏷️ Catégorie</label>
              <select className="option-select">
                <option>Article Expertise</option>
                <option>Analyse Technique</option>
                <option>Réflexion Métier</option>
                <option>Méthodologie</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn-secondary" onClick={() => setShowPublishModal(false)}>
            Annuler
          </button>
          <button className="btn-primary" onClick={async () => {
            try {
              const token = localStorage.getItem('oracle_token');
              const destinations = [];
              const checkboxes = document.querySelectorAll('.destination-option input[type="checkbox"]');
              const destNames = ['expertise', 'blog', 'email', 'archive'];
              checkboxes.forEach((cb, i) => { if (cb.checked) destinations.push(destNames[i]); });
              const category = document.querySelector('.publish-options .option-select')?.value || 'Article Expertise';
              const publishMode = document.querySelectorAll('.publish-options .option-select')[1]?.value?.toLowerCase() || 'immediate';
              await axios.post(`/api/projects/${projectId}/publish`, {
                content: `Conversation projet #${projectId}`,
                destinations,
                category,
                publishMode,
              }, { headers: { Authorization: `Bearer ${token}` } });
              toast.success('Publication envoyée à N8N !');
            } catch (err) {
              toast.error(err.response?.data?.error || 'Erreur publication');
            }
            setShowPublishModal(false);
          }}>
            Lancer Publication
          </button>
        </div>
      </div>
    </div>
  );

  // Modal Réseaux Sociaux
  const SocialModal = () => (
    <div className="modal-overlay" onClick={() => setShowSocialModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>📱 Diffusion Réseaux Sociaux</h3>
          <button className="modal-close" onClick={() => setShowSocialModal(false)}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="social-platforms">
            <label className="platform-option">
              <input type="checkbox" defaultChecked />
              <div className="platform-info">
                <span className="platform-name">📘 Facebook</span>
                <span className="platform-desc">Page de votre marque + engagement</span>
              </div>
            </label>
            
            <label className="platform-option">
              <input type="checkbox" defaultChecked />
              <div className="platform-info">
                <span className="platform-name">📸 Instagram</span>
                <span className="platform-desc">Format carré + citation + visuels ITRIIA</span>
              </div>
            </label>
            
            <label className="platform-option">
              <input type="checkbox" />
              <div className="platform-info">
                <span className="platform-name">🐦 X (Twitter)</span>
                <span className="platform-desc">Thread optimisé + hashtags #ConscienceMultidimensionnelle</span>
              </div>
            </label>
            
            <label className="platform-option">
              <input type="checkbox" />
              <div className="platform-info">
                <span className="platform-name">🔗 LinkedIn</span>
                <span className="platform-desc">Contenu professionnel ITRIIA</span>
              </div>
            </label>
          </div>
          
          <div className="social-options">
            <div className="option-group">
              <label>⏰ Programmation</label>
              <select className="option-select">
                <option>Immédiat</option>
                <option>Heure optimale (17h)</option>
                <option>Demain matin (9h)</option>
                <option>Weekend (Samedi 10h)</option>
              </select>
            </div>
            
            <div className="option-group">
              <label>🎯 Format</label>
              <select className="option-select">
                <option>Citation + visuel</option>
                <option>Thread détaillé</option>
                <option>Story éphémère</option>
                <option>Article complet</option>
              </select>
            </div>
          </div>
          
          <div className="hashtags-preview">
            <label>🏷️ Hashtags générés</label>
            <div className="hashtags">
              #ITRIIA #ExpertiseMétier #OracleTRYA #IA #Conscience #Intelligence
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn-secondary" onClick={() => setShowSocialModal(false)}>
            Annuler
          </button>
          <button className="btn-primary" onClick={async () => {
            try {
              const token = localStorage.getItem('oracle_token');
              const platforms = [];
              const checkboxes = document.querySelectorAll('.platform-option input[type="checkbox"]');
              const platNames = ['facebook', 'instagram', 'twitter', 'linkedin'];
              checkboxes.forEach((cb, i) => { if (cb.checked) platforms.push(platNames[i]); });
              const schedule = document.querySelector('.social-options .option-select')?.value || 'immediate';
              const tone = document.querySelectorAll('.social-options .option-select')[1]?.value || 'professional';
              await axios.post(`/api/projects/${projectId}/social`, {
                content: `Contenu projet #${projectId}`,
                platforms,
                tone,
                schedule,
              }, { headers: { Authorization: `Bearer ${token}` } });
              toast.success('Diffusion sociale envoyée à N8N !');
            } catch (err) {
              toast.error(err.response?.data?.error || 'Erreur diffusion');
            }
            setShowSocialModal(false);
          }}>
            Programmer Diffusion
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="input-toolbar" style={{ position: 'relative' }}>
      <style>{`
       .input-toolbar {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 0 6px 0;
  background: transparent;
  border-bottom: none;
  justify-content: flex-start;
  flex-wrap: wrap;
}

        .toolbar-tool {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 5px 10px;
          background: var(--card-bg);
          border: 1px solid var(--card-border);
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 11px;
          font-weight: 500;
          color: var(--text-secondary);
          position: relative;
          overflow: hidden;
        }

        .toolbar-tool.new-feature {
          border-color: var(--accent);
          background: var(--accent-light);
          animation: subtle-pulse 2s infinite;
        }

        @keyframes subtle-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(72, 136, 224, 0.1); }
          50% { box-shadow: 0 0 0 4px rgba(72, 136, 224, 0.05); }
        }

        .toolbar-tool:hover {
          transform: translateY(-1px);
          box-shadow: var(--shadow-lg);
          border-color: var(--accent);
          color: var(--accent);
        }

        .toolbar-tool:active {
          transform: translateY(0);
        }

        .toolbar-tool::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          transition: left 0.5s ease;
        }

        .toolbar-tool:hover::before {
          left: 100%;
        }

        .tool-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .toolbar-tool:hover .tool-icon {
          transform: scale(1.1);
        }

        .tool-label {
          font-size: 12px;
          white-space: nowrap;
        }

        .context-indicator {
          margin-left: auto;
          font-size: 10px;
          color: var(--text-muted);
          padding: 3px 6px;
          background: var(--card-bg);
          border-radius: 4px;
          border-left: 2px solid var(--accent);
        }

        /* Modales */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--overlay-bg);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: var(--modal-bg);
          border: 1px solid var(--modal-border);
          border-radius: 16px;
          box-shadow: var(--shadow-lg);
          max-width: 600px;
          width: 90vw;
          max-height: 80vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid var(--card-border);
        }

        .modal-header h3 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text);
          margin: 0;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: var(--text-secondary);
          padding: 4px;
          line-height: 1;
        }

        .modal-body {
          padding: 24px;
        }

        .destination-option, .platform-option {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          border: 1px solid var(--card-border);
          border-radius: 12px;
          margin-bottom: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .destination-option:hover, .platform-option:hover {
          border-color: var(--accent);
          background: var(--accent-light);
        }

        .destination-info, .platform-info {
          flex: 1;
        }

        .destination-name, .platform-name {
          display: block;
          font-weight: 500;
          color: var(--text);
          margin-bottom: 4px;
        }

        .destination-desc, .platform-desc {
          font-size: 14px;
          color: var(--text-secondary);
        }

        .publish-options, .social-options {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-top: 24px;
        }

        .option-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .option-group label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text);
        }

        .option-select {
          padding: 8px 12px;
          border: 1px solid var(--input-border);
          border-radius: 8px;
          background: var(--input-bg);
          color: var(--text);
          font-size: 14px;
        }

        .hashtags-preview {
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid var(--card-border);
        }

        .hashtags {
          background: var(--input-bg);
          padding: 12px;
          border-radius: 8px;
          font-size: 14px;
          color: var(--text);
          margin-top: 8px;
        }

        .modal-footer {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          padding: 20px 24px;
          border-top: 1px solid var(--card-border);
        }

        .btn-secondary {
          padding: 10px 20px;
          background: var(--btn-secondary-bg);
          border: 1px solid var(--btn-secondary-border);
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          color: var(--btn-secondary-text);
        }

        .btn-primary {
          padding: 10px 20px;
          background: linear-gradient(135deg, var(--accent) 0%, var(--accent-hover) 100%);
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          color: white;
        }

        .btn-primary:hover {
          opacity: 0.9;
        }

        /* Sélecteur de modèle */
        .model-selector-wrapper {
          position: relative;
        }

        .model-selector-btn {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .model-forced {
          border-color: var(--accent) !important;
          background: var(--accent-light) !important;
          color: var(--accent) !important;
        }

        .model-streaming {
          animation: modelGlow 1.8s ease-in-out infinite;
          border-color: var(--accent) !important;
          color: var(--accent) !important;
        }

        @keyframes modelGlow {
          0%, 100% {
            box-shadow: 0 0 4px rgba(37, 99, 235, 0.3);
            background: var(--accent-light);
          }
          50% {
            box-shadow: 0 0 16px rgba(37, 99, 235, 0.6), 0 0 32px rgba(37, 99, 235, 0.2);
            background: rgba(37, 99, 235, 0.15);
          }
        }

        .model-dropdown {
          position: absolute;
          bottom: 100%;
          left: 0;
          margin-bottom: 4px;
          background: var(--modal-bg);
          border: 1px solid var(--card-border);
          border-radius: 12px;
          box-shadow: var(--shadow-lg);
          min-width: 260px;
          max-height: 400px;
          overflow-y: auto;
          z-index: 100;
          padding: 6px 0;
        }

        .model-dropdown-divider {
          height: 1px;
          background: var(--card-border);
          margin: 4px 0;
        }

        .model-group-header {
          font-size: 11px;
          font-weight: 600;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 8px 16px 4px;
        }

        .model-option {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 8px 16px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 13px;
          color: var(--text);
          text-align: left;
          transition: background 0.15s;
        }

        .model-option:hover {
          background: var(--conv-item-hover);
        }

        .model-option-active {
          background: var(--accent-light) !important;
          color: var(--accent);
          font-weight: 600;
        }

        .model-option-auto {
          font-weight: 500;
          color: #059669;
        }

        .model-badge-img {
          font-size: 9px;
          font-weight: 700;
          background: #8b5cf6;
          color: white;
          padding: 1px 5px;
          border-radius: 4px;
          margin-left: auto;
        }

        @media (max-width: 768px) {
          .input-toolbar {
            flex-wrap: wrap;
            gap: 6px;
          }

          .toolbar-tool {
            padding: 4px 6px;
          }

          .tool-label {
            display: none;
          }

          .modal-content {
            width: 95vw;
          }

          .publish-options, .social-options {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="input-toolbar">

        {/* Outils contextuels */}
        {tools.map((tool) => {
          let extraStyle = {};
          if (tool.isActive) {
            extraStyle = {
              background: `${tool.color}18`,
              borderColor: tool.color,
              color: tool.color,
              boxShadow: `0 0 8px ${tool.color}30`,
            };
          } else if (tool.isFeatured) {
            extraStyle = {
              background: `${tool.color}10`,
              borderColor: `${tool.color}55`,
              boxShadow: `0 0 10px ${tool.color}22`,
            };
          }
          return (
            <button
              key={tool.id}
              className={`toolbar-tool ${tool.isNew ? 'new-feature' : ''} ${tool.isActive ? 'tool-active' : ''}`}
              onClick={() => tool.action && tool.action()}
              title={tool.description}
              style={extraStyle}
            >
              <div className="tool-icon" style={{ color: tool.color }}>
                {tool.icon}
              </div>
              <span className="tool-label">{tool.label}</span>
            </button>
          );
        })}

        {/* Sélecteur de modèle */}
        {onForcedModelChange && (
          <div className="model-selector-wrapper" ref={modelDropdownRef}>
            <button
              className={`toolbar-tool model-selector-btn ${forcedModel ? 'model-forced' : ''} ${isStreaming ? 'model-streaming' : ''}`}
              onClick={() => setShowModelDropdown(!showModelDropdown)}
              title={forcedModel ? `Modèle forcé: ${selectedModelLabel}` : 'ITRIIA Route — sélection automatique'}
            >
              <span>{forcedModel ? '🎯' : '🤖'}</span>
              <span className="tool-label">{selectedModelLabel}</span>
              <ChevronDown size={12} />
            </button>

            {showModelDropdown && (
              <div className="model-dropdown">
                <button
                  className={`model-option model-option-auto ${!forcedModel ? 'model-option-active' : ''}`}
                  onClick={() => { onForcedModelChange(null); setShowModelDropdown(false); }}
                >
                  🤖 Auto (ITRIIA Route)
                </button>
                <div className="model-dropdown-divider" />
                {thinkingModels.length > 0 && (
                  <div key="__thinking">
                    <div className="model-group-header">🧠 Thinking</div>
                    {thinkingModels.map(model => {
                      const mid = model.model_id || model.id;
                      return (
                        <button
                          key={mid}
                          className={`model-option ${forcedModel === mid ? 'model-option-active' : ''}`}
                          onClick={() => handleModelSelect(mid)}
                        >
                          {model.label}
                          {(model.supports_image_gen || model.type === 'text+image') && <span className="model-badge-img">IMG</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
                {Object.entries(groupedModels).map(([provider, models]) => (
                  <div key={provider}>
                    <div className="model-group-header">{provider}</div>
                    {models.map(model => {
                      const mid = model.model_id || model.id;
                      return (
                        <button
                          key={mid}
                          className={`model-option ${forcedModel === mid ? 'model-option-active' : ''}`}
                          onClick={() => handleModelSelect(mid)}
                        >
                          {model.label}
                          {(model.supports_image_gen || model.type === 'text+image') && <span className="model-badge-img">IMG</span>}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Indicateur contexte supprimé — redondant dans le chat projet */}
      </div>
      
      {/* Dropdown Templates */}
      {showTemplates && (
        <div ref={templatesRef} style={{
          position: 'absolute', bottom: '100%', left: 0,
          width: '425px', marginBottom: '4px', background: 'var(--card-bg)',
          border: '1px solid var(--card-border)', borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          zIndex: 100, maxHeight: '760px', display: 'flex', flexDirection: 'column',
        }}>
          {/* Header avec titre + bouton gérer */}
          <div style={{
            padding: '12px 14px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            borderBottom: '1px solid var(--card-border)',
          }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>
                {userTemplates.length > 0 ? 'Mes raccourcis' : 'Raccourcis'}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                Cliquez pour insérer, survolez pour aperçu
              </div>
            </div>
            <button
              onClick={() => { setShowTemplates(false); onManageTemplates && onManageTemplates(); }}
              title="Créer et gérer mes raccourcis personnels"
              style={{
                background: 'rgba(29,78,216,0.1)', border: '1px solid rgba(29,78,216,0.25)',
                borderRadius: '6px', padding: '5px 10px', cursor: 'pointer',
                color: '#3b82f6', fontSize: '11px', fontWeight: 500,
                display: 'flex', alignItems: 'center', gap: '4px',
                transition: 'all 0.15s', flexShrink: 0,
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(29,78,216,0.2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(29,78,216,0.1)'}
            >
              <Settings size={11} /> Personnaliser
            </button>
          </div>
          {/* Liste templates avec aperçu au hover */}
          <div style={{ padding: '8px 12px', overflowY: 'auto', flex: 1 }}>
            {templates.map((cat) => (
              <div key={cat.category} style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: '#06b6d4', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                  {cat.category}
                </div>
                {cat.items.map((t) => (
                  <div key={t.label} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                    className="template-item-wrap"
                    onMouseEnter={e => { e.currentTarget.querySelector('.template-preview')?.style.setProperty('display', 'block'); e.currentTarget.querySelector('.tpl-edit')?.style.setProperty('opacity', '1'); }}
                    onMouseLeave={e => { e.currentTarget.querySelector('.template-preview')?.style.setProperty('display', 'none'); e.currentTarget.querySelector('.tpl-edit')?.style.setProperty('opacity', '0'); }}
                  >
                    <button onClick={() => { onInsertTemplate(t.prompt); setShowTemplates(false); }}
                      style={{
                        flex: 1, textAlign: 'left', padding: '8px 10px',
                        background: 'transparent', border: 'none', borderRadius: '6px',
                        cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '12px',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--input-bg)'; e.currentTarget.style.color = 'var(--text)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                    >
                      {t.label}
                    </button>
                    <button className="tpl-edit"
                      onClick={(e) => { e.stopPropagation(); setShowTemplates(false); onEditTemplate && onEditTemplate({ title: t.label, content: t.prompt, category: cat.category.replace('★ ', ''), id: t.id || null }); }}
                      title={t.isSystem ? 'Personnaliser ce template' : 'Modifier'}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--text-secondary)', padding: '4px', flexShrink: 0,
                        opacity: 0, transition: 'opacity 0.15s',
                      }}
                    >
                      <Pencil size={11} />
                    </button>
                    {/* Aperçu du prompt au hover */}
                    <div className="template-preview" style={{
                      display: 'none', position: 'absolute', left: 0, top: '100%', marginTop: '2px',
                      width: '100%', padding: '8px 10px', background: 'var(--input-bg)',
                      border: '1px solid var(--card-border)', borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 110,
                      fontSize: '11px', lineHeight: '1.5', color: 'var(--text-secondary)',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      pointerEvents: 'none', maxHeight: '80px', overflow: 'hidden',
                    }}>
                      {t.prompt}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modales */}
      {showPublishModal && <PublishModal />}
      {showSocialModal && <SocialModal />}
    </div>
  );
};

export default InputToolbar;
